import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tags, ToggleLeft, ToggleRight, Save } from "lucide-react";
import { fetchOfferConfig, updateOfferPricing, updateFirstOffer } from "@admin/services/adminApi";
import { PageHeader, Pill } from "@admin/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function NumberField({ id, label, value, onChange, suffix = "₹", testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <div className="relative mt-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>
        <Input id={id} type="number" min={0} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="pl-7" data-testid={testid} />
      </div>
    </div>
  );
}

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [global, setGlobal] = useState({ chat: 0, audio: 0, video: 0 });
  const [first, setFirst] = useState({
    enabled: false,
    offer_type: "flat",
    chat_price: 49, audio_price: 99, video_price: 149,
    chat_pct: 50, audio_pct: 50, video_pct: 50,
    label: "First Consultation Offer",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const c = await fetchOfferConfig();
      setCfg(c);
      setGlobal({ chat: c.chat, audio: c.audio, video: c.video });
      const fco = c.first_consultation_offer || {};
      setFirst({
        enabled: !!fco.enabled,
        offer_type: fco.offer_type || "flat",
        chat_price: fco.chat_price ?? 49,
        audio_price: fco.audio_price ?? 99,
        video_price: fco.video_price ?? 149,
        chat_pct: fco.chat_pct ?? 50,
        audio_pct: fco.audio_pct ?? 50,
        video_pct: fco.video_pct ?? 50,
        label: fco.label || "First Consultation Offer",
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load pricing"); }
  };
  useEffect(() => { load(); }, []);

  const saveGlobal = async () => {
    setSaving(true);
    try {
      await updateOfferPricing(global);
      toast.success("Global offer updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const saveFirst = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: first.enabled,
        offer_type: first.offer_type,
        label: first.label,
        ...(first.offer_type === "flat"
          ? { chat_price: first.chat_price, audio_price: first.audio_price, video_price: first.video_price }
          : { chat_pct: first.chat_pct, audio_pct: first.audio_pct, video_pct: first.video_pct }),
      };
      await updateFirstOffer(payload);
      toast.success("First-Consultation offer updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const toggleFirst = async () => {
    setSaving(true);
    try {
      await updateFirstOffer({ enabled: !first.enabled });
      toast.success(!first.enabled ? "First-Consultation offer enabled" : "First-Consultation offer disabled");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
    setSaving(false);
  };

  if (!cfg) return <div className="text-slate-500" data-testid="pricing-loading">Loading…</div>;

  return (
    <>
      <PageHeader
        testid="admin-pricing-header"
        title="Pricing & Offers"
        subtitle="Centralized config — applies to every lawyer & consultation type. Precedence: First-Consult > Global > Original."
      />

      {/* Global Discount */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="global-offer-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Tier 2</p>
            <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Global Discount Offer</h2>
            <p className="text-xs text-slate-500">Applied to every paying user when First-Consult tier doesn't apply.</p>
          </div>
          <Pill color="blue">Always on</Pill>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <NumberField id="g-chat" label="Chat price" value={global.chat} onChange={(v) => setGlobal({ ...global, chat: v })} testid="global-chat-input" />
          <NumberField id="g-audio" label="Audio price" value={global.audio} onChange={(v) => setGlobal({ ...global, audio: v })} testid="global-audio-input" />
          <NumberField id="g-video" label="Video price" value={global.video} onChange={(v) => setGlobal({ ...global, video: v })} testid="global-video-input" />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveGlobal} disabled={saving} data-testid="save-global-btn" className="rounded-xl adv-btn-primary font-bold">
            <Save size={14} /> Save global offer
          </Button>
        </div>
      </section>

      {/* First Consultation Offer */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="first-offer-card">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]">Tier 1 · Highest priority</p>
            <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              <Tags size={16} className="inline mr-1" /> First Consultation Offer
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Shown to first-time users (0 completed consultations) when enabled. Overrides Global Offer.
            </p>
          </div>
          <button onClick={toggleFirst} disabled={saving} data-testid="toggle-first-offer" className="flex items-center gap-2">
            {first.enabled
              ? <><ToggleRight size={36} className="text-emerald-600" /><span className="text-xs font-bold text-emerald-700">ENABLED</span></>
              : <><ToggleLeft size={36} className="text-slate-300" /><span className="text-xs font-bold text-slate-400">DISABLED</span></>}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {["flat", "percent"].map((t) => (
            <button
              key={t}
              onClick={() => setFirst({ ...first, offer_type: t })}
              data-testid={`first-type-${t}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                first.offer_type === t ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
              }`}
            >
              {t === "flat" ? "Flat fixed prices" : "Percent off original"}
            </button>
          ))}
        </div>

        {first.offer_type === "flat" ? (
          <div className="grid sm:grid-cols-3 gap-3">
            <NumberField id="f-chat" label="Chat price" value={first.chat_price} onChange={(v) => setFirst({ ...first, chat_price: v })} testid="first-chat-price" />
            <NumberField id="f-audio" label="Audio price" value={first.audio_price} onChange={(v) => setFirst({ ...first, audio_price: v })} testid="first-audio-price" />
            <NumberField id="f-video" label="Video price" value={first.video_price} onChange={(v) => setFirst({ ...first, video_price: v })} testid="first-video-price" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-3">
            <NumberField id="f-chat-p" label="Chat % off" value={first.chat_pct} onChange={(v) => setFirst({ ...first, chat_pct: v })} suffix="%" testid="first-chat-pct" />
            <NumberField id="f-audio-p" label="Audio % off" value={first.audio_pct} onChange={(v) => setFirst({ ...first, audio_pct: v })} suffix="%" testid="first-audio-pct" />
            <NumberField id="f-video-p" label="Video % off" value={first.video_pct} onChange={(v) => setFirst({ ...first, video_pct: v })} suffix="%" testid="first-video-pct" />
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3 justify-between">
          <p className="text-[11px] text-slate-400">Applicable once per user · Eligibility = 0 completed consultations</p>
          <Button onClick={saveFirst} disabled={saving} data-testid="save-first-offer-btn" className="rounded-xl adv-btn-gold font-bold">
            <Save size={14} /> Save & apply
          </Button>
        </div>
      </section>
    </>
  );
}
